/**
 * Seeded PRNG.
 *
 * Math.random() is banned in generation code (lint-enforced): a session must be
 * reproducible from its seed, and tests must be able to assert on exact output.
 *
 * Streams are separate so that changing how many numbers one engine draws does
 * not shift every other engine's output. Adding a stream is safe; reordering
 * draws within a stream is not.
 */

export type RngStreamName = "section" | "rhythm" | "performance" | "rare" | "texture";

export interface Rng {
  /** Uniform [0, 1). */
  next(): number;
  /** Uniform integer in [min, max]. */
  int(min: number, max: number): number;
  /** True with the given probability. */
  chance(probability: number): boolean;
  /** Approximately normal, mean 0, standard deviation 1 (sum of uniforms). */
  gaussian(): number;
  /** Index into `weights`, chosen proportionally. Falls back to the last index. */
  weighted(weights: readonly number[]): number;
  /** Picks one element using per-element weights. */
  pick<T>(items: readonly T[], weightOf: (item: T, index: number) => number): T;
}

/** 32-bit string hash (FNV-1a), so any seed string maps to a usable integer. */
export function hashSeed(seed: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** mulberry32: small, fast, good enough for musical decisions. */
function mulberry32(seedValue: number): () => number {
  let a = seedValue >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function createRng(seedValue: number): Rng {
  const next = mulberry32(seedValue);

  const rng: Rng = {
    next,
    int(min: number, max: number) {
      if (max <= min) return min;
      return min + Math.floor(next() * (max - min + 1));
    },
    chance(probability: number) {
      return next() < probability;
    },
    gaussian() {
      // Irwin–Hall with n=4, centred and scaled. Cheap and bounded, which is
      // what we want for timing jitter — no unbounded tails.
      const sum = next() + next() + next() + next();
      return (sum - 2) * 1.732;
    },
    weighted(weights: readonly number[]) {
      let total = 0;
      for (const w of weights) total += Math.max(0, w);
      if (total <= 0) return weights.length - 1;
      let roll = next() * total;
      for (let i = 0; i < weights.length; i += 1) {
        roll -= Math.max(0, weights[i] ?? 0);
        if (roll <= 0) return i;
      }
      return weights.length - 1;
    },
    pick<T>(items: readonly T[], weightOf: (item: T, index: number) => number): T {
      const weights = items.map(weightOf);
      const index = rng.weighted(weights);
      // items is never empty at call sites; the fallback keeps the type honest.
      return items[index] ?? (items[0] as T);
    },
  };

  return rng;
}

/** One independent stream per concern, all derived from the session seed. */
export type RngStreams = Record<RngStreamName, Rng>;

const STREAM_NAMES: RngStreamName[] = ["section", "rhythm", "performance", "rare", "texture"];

export function createStreams(seed: string): RngStreams {
  const base = hashSeed(seed);
  const streams = {} as RngStreams;
  STREAM_NAMES.forEach((name, index) => {
    // Offset per stream so the streams do not correlate.
    streams[name] = createRng((base + index * 0x9e3779b9) >>> 0);
  });
  return streams;
}

/**
 * Selects `count` distinct indices, preferring higher weights.
 *
 * This is how rhythm patterns are populated: never a per-step coin flip, but a
 * weighted choice over a metric profile, so the result follows the musical
 * weight of the bar rather than uniform noise.
 */
export function selectWeightedDistinct(
  weights: readonly number[],
  count: number,
  rng: Rng,
): number[] {
  const remaining = weights.map((w, index) => ({ index, weight: Math.max(0.0001, w) }));
  const chosen: number[] = [];
  const target = Math.min(count, remaining.length);

  for (let n = 0; n < target; n += 1) {
    let total = 0;
    for (const entry of remaining) total += entry.weight;
    let roll = rng.next() * total;
    let pickedAt = remaining.length - 1;
    for (let i = 0; i < remaining.length; i += 1) {
      roll -= remaining[i]?.weight ?? 0;
      if (roll <= 0) {
        pickedAt = i;
        break;
      }
    }
    const picked = remaining[pickedAt];
    if (picked === undefined) break;
    chosen.push(picked.index);
    remaining.splice(pickedAt, 1);
  }

  return chosen.sort((a, b) => a - b);
}
